import React, { useState, useMemo } from 'react';
import {
  PlusIcon,
  DocumentTextIcon,
  ClockIcon,
  CurrencyDollarIcon,
  MapPinIcon,
  UserCircleIcon,
  FunnelIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

const PrequalificationTabDetails = ({ prequalifications, handleCardClick }) => {
  const navigate = useNavigate();
  const [selectedBroker, setSelectedBroker] = useState('all');

  // Get unique brokers from prequalifications
  const brokers = useMemo(() => {
    const uniqueBrokers = new Set();
    prequalifications.forEach(p => {
      if (p.brokerName && p.brokerName !== 'Unknown Broker') {
        uniqueBrokers.add(p.brokerName);
      }
    });
    
    const sortedBrokers = Array.from(uniqueBrokers).sort();
    console.log('Available brokers:', sortedBrokers);
    return ['all', ...sortedBrokers];
  }, [prequalifications]);

  // Filter prequalifications by selected broker
  const filteredPrequalifications = useMemo(() => {
    console.log('Filtering by broker:', selectedBroker);
    if (selectedBroker === 'all') return prequalifications;
    return prequalifications.filter(p => p.brokerName === selectedBroker);
  }, [prequalifications, selectedBroker]);

  const formatCurrency = (amount) => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    if (date.toDate) {
      return date.toDate().toLocaleDateString();
    }
    return new Date(date).toLocaleDateString();
  };

  const handlePrequalClick = (application) => {
    navigate(`/prequalification-details/${application.id}`);
  };

  // Otherwise show the regular cards view
  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Filter Section */}
      <div className="mb-6 bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm text-gray-700">Filter by Broker:</span>
          </div>
          <select
            value={selectedBroker}
            onChange={(e) => setSelectedBroker(e.target.value)}
            className="block w-64 rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6"
          >
            <option value="all">All Brokers</option>
            {brokers
              .filter(broker => broker !== 'all')
              .sort()
              .map(broker => (
                <option key={broker} value={broker}>
                  {broker}
                </option>
              ))}
          </select>
          <div className="text-sm text-gray-500">
            Showing {filteredPrequalifications.length} of {prequalifications.length} applications
          </div>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredPrequalifications.map((application) => (
          <div 
            key={application.id}
            className={`w-full rounded-xl overflow-hidden shadow-lg border ${
              application.prequalifiedAmount 
                ? 'border-emerald-500 bg-emerald-50' 
                : 'border-gray-200 bg-white'
            } cursor-pointer`}
            onClick={() => handlePrequalClick(application)}
          >
            <div className={`p-6 border-b ${
              application.prequalifiedAmount 
                ? 'border-emerald-200 bg-emerald-50' 
                : 'border-gray-100'
            }`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-800">
                  {application.businessName || 'Unnamed Business'}
                </h3>
              </div>
              <div className="flex items-center text-gray-600 text-sm mb-3">
                <MapPinIcon className="w-4 h-4 mr-2 text-blue-500" />
                <p>{application.businessState || 'Location not specified'}</p>
              </div>
              <div className="flex items-center text-gray-600 text-sm">
                <UserCircleIcon className="w-4 h-4 mr-2 text-blue-500" />
                <p>{application.brokerName}</p>
              </div>
            </div>
            
            <div className={`p-6 space-y-4 ${
              application.prequalifiedAmount 
                ? 'bg-emerald-50' 
                : 'bg-white'
            }`}>
              <div className="flex items-center justify-between">
                <span className={`${
                  application.prequalifiedAmount 
                    ? 'text-emerald-700' 
                    : 'text-gray-600'
                }`}>
                  {application.prequalifiedAmount ? 'Prequalified Amount' : 'Purchase Price'}
                </span>
                <div className="flex items-center">
                  <CurrencyDollarIcon className={`w-5 h-5 mr-1 ${
                    application.prequalifiedAmount 
                      ? 'text-emerald-600' 
                      : 'text-gray-600'
                  }`} />
                  <span className={`text-2xl font-bold ${
                    application.prequalifiedAmount 
                      ? 'text-emerald-700' 
                      : 'text-gray-800'
                  }`}>
                    {application.prequalifiedAmount 
                      ? formatCurrency(application.prequalifiedAmount)
                      : (application.purchasePrice 
                          ? formatCurrency(application.purchasePrice) 
                          : 'N/A')}
                  </span>
                </div>
              </div>
              <div className="rounded-lg p-4 space-y-2 bg-blue-50">
                <div className="flex items-center justify-between">
                  <span className="text-blue-700 font-medium">Documents Uploaded</span>
                  <div className="flex items-center text-gray-800">
                    <DocumentTextIcon className="w-4 h-4 mr-2 text-blue-500" />
                    {application.documents ? application.documents.length : 0}
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`px-6 py-4 flex items-center justify-between ${
              application.prequalifiedAmount 
                ? 'bg-emerald-100' 
                : 'bg-gray-50'
            }`}>
              <span className="text-gray-600 text-sm">Last Updated</span>
              <div className="flex items-center text-gray-700 text-sm">
                <ClockIcon className="w-4 h-4 mr-2 text-blue-500" />
                {formatDate(application.lastUpdated)}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PrequalificationTabDetails;